import { useState, useRef, useCallback, useEffect } from 'react';
import { gameLogger } from '../../utils/GameLogger';
import { Helix2000Character } from '../../shared/CharacterTypes';
import type { GameScene } from '../scenes/GameScene';
import type { NetworkManager } from '../network/NetworkManager';
import { ChatMessage } from '../ui/HUD/ChatTypes';
import { chatHandler } from '../ui/contexts/ChatContext';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { CombatLogService } from '../systems/combat-log/CombatLogService';
import { CombatLogChatBridge } from '../systems/combat-log/CombatLogChatBridge';
import { useColyseusMessages } from '../ui/hooks/useColyseusMessages';
import { ServiceRegistry } from '../core/ServiceRegistry';
import type {
  ServerHotbarSnapshotAuthority,
} from '../systems/player/PlayerManager';
import type { Room } from '@colyseus/sdk';
import { toReactQuest } from './useServerConnection/toReactQuest';
import { mergeAcceptedQuest, type QuestAcceptedPayload } from './questAcceptedState';
import { DevConfig } from '../../config/environment/devConfig';
import { projectTalentTree } from '../../shared/progression/talents';
import { normalizeKnownPlaces } from '../../shared/logic/knownPlaces';
import { normalizeClaimedRewards } from '../../shared/logic/claimedRewards';
import { keepUnchangedHeavyFields, projectPeriodicSync, projectPlayerSchema } from './useServerConnection/projectPlayerSchema';
import { writePlayerVitals } from '../systems/player/livePlayerVitals';
import { teleportChatLine, type TeleportChatMessage } from './useServerConnection/teleportChatLine';
import { joinRefusalFromReason } from '../network/joinRefusal';
import { registerProgressionEvents } from './useServerConnection/progressionEvents';
export { abilityTrainingAvailableText, trainerResultText, talentResultText } from './useServerConnection/progressionEvents';
import { CooldownStore } from '../systems/player/CooldownStore';
import { applyChargeMessage } from './useServerConnection/chargeMessages';
import { pushHotbarSnapshotToCastSource, readAtomicHotbarAuthority, pushInventoryToSystem } from './useServerConnection/snapshotProjection';
import type {
  PlayerSchemaView,
  InventoryUpdateMessage,
  HotbarUpdateMessage,
  QuestsUpdateMessage,
  QuestProgressWire,
  CompletedQuestsUpdateMessage,
  HeavyFieldsSnapshotMessage,
  NetConnectedPayload,
  NetDisconnectedPayload,
  PeriodicSyncPayload,
  GoldReceivedMessage,
  LootReceivedMessage,
  LevelUpMessage,
  QuestCompletedMessage,
} from '../network/ClientMessageTypes';

export { toReactQuest } from './useServerConnection/toReactQuest';

interface UseServerConnectionOptions {
  player: Helix2000Character | null;
  gameWorld: GameScene | null;
  authToken: string | null;
  setPlayer: React.Dispatch<React.SetStateAction<Helix2000Character | null>>;
  setChatMessages?: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
}

export function useServerConnection({
  player,
  gameWorld,
  authToken,
  setPlayer,
  setChatMessages: _setChatMessages,
}: UseServerConnectionOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState<string | null>(null);
  const [connectedSessionId, setConnectedSessionId] = useState<string | null>(null);
  const [hotbarHydratedSessionId, setHotbarHydratedSessionId] = useState<string | null>(null);
  const activeHeavyFieldsSessionRef = useRef<string | null>(null);
  const activeHotbarAuthorityRef = useRef<ServerHotbarSnapshotAuthority | null>(null);
  const isConnecting = useRef(false);
  const joinRefusal = joinRefusalFromReason(disconnectReason); // fails the same on every retry

  const connectToServer = useCallback(async () => {
    if (isConnecting.current || isConnected || joinRefusal) return;

    try {
      isConnecting.current = true;
      if (!authToken || !player?._id) {
        throw new Error('Missing authentication data');
      }

      // Bootstrap the network stack early so the connection can be made
      // before GameScene (and its heavy import tree) is loaded. This moves
      // GameScene's v8.evaluateModule out of the pre-room freeze window.
      if (!(window as any).nm || !ServiceRegistry.getInstance().get('NetworkSyncSystem', { optional: true })) {
        const [{ NetworkManager }, { NetworkSyncSystem }] = await Promise.all([
          import('../network/NetworkManager'),
          import('../systems/network/NetworkSyncSystem'),
        ]);
        const nm = ((window as any).nm as NetworkManager | undefined) ?? new NetworkManager();
        (window as any).nm = nm;
        if (!ServiceRegistry.getInstance().get('NetworkSyncSystem', { optional: true })) {
          const nss = new NetworkSyncSystem(nm);
          nss.init();
        }
      }

      gameLogger.info('SYSTEM', '🔌 [React] Requesting connection via EventBus', { authToken: authToken.substring(0, 5), playerId: player._id });
      EventBus.getInstance().emit('net:connectIntent', {
        authToken,
        playerId: player._id,
        playerClass: player.class,
        playerName: player.name,
      });

    } catch (error) {
      gameLogger.warn('network', '⚠️ Failed to initiate connection:', error);
      setIsConnected(false);
      isConnecting.current = false;
    }
  }, [player, authToken, isConnected, joinRefusal]);

  // Latest-player + latest-gameWorld refs. The big EventBus subscription
  // effect below uses both only as runtime read-throughs (never as
  // value-deps), so reading them via refs keeps them out of the deps
  // array. Without this, every `setPlayer` call from any of the ~20
  // subscribed handlers re-ran the effect → tore down + re-bound all
  // ~20 EventBus subscriptions. Same bug class as useGameEvents (fixed
  // 2026-05-02) — same fix.
  const playerRef = useRef(player);
  playerRef.current = player;
  const gameWorldRef = useRef(gameWorld);
  gameWorldRef.current = gameWorld;

  // Phase 3 of WS 1009 state-bloat arc (2026-05-16): the four heavy
  // Player schema fields (inventory/hotbar/quests/completedQuests) no
  // longer sync via Colyseus state — server pushes their incremental
  // updates via direct messages. These handlers map the wire shape onto
  // the Helix2000Character React-state shape. The connection-critical
  // heavyFieldsSnapshot is intentionally NOT registered here: the generic
  // hook discovers rooms by polling every 500 ms. NetworkSyncSystem binds
  // and relays that message before requesting the first snapshot instead.
  useColyseusMessages({
    inventoryUpdate: (msg: InventoryUpdateMessage) => {
      if (!msg) return;
      pushInventoryToSystem(msg);
      setPlayer(prev => prev ? {
        ...prev,
        inventory: {
          ...prev.inventory,
          items: Array.isArray(msg.items) ? msg.items : prev.inventory?.items ?? [],
          gold: typeof msg.gold === 'number' ? msg.gold : prev.inventory?.gold ?? 0,
        },
      } : null);
    },
    hotbarUpdate: (msg: HotbarUpdateMessage) => {
      if (!msg || !Array.isArray(msg.slots)) return;
      // Multiplayer incremental updates cannot establish initial authority.
      // Wait for the atomic heavy snapshot for this session first.
      if (
        DevConfig.systems.multiplayer
        && activeHotbarAuthorityRef.current?.sessionId !== activeHeavyFieldsSessionRef.current
      ) {
        return;
      }
      const slots = msg.slots; // narrowed by the Array.isArray guard above
      // Cast source is fed by the always-live net:message:hotbarUpdate relay
      // (NetworkSyncSystem DIRECT_RELAY_TYPES → PlayerManager twin) — this
      // handler only updates the React HUD view.
      setPlayer(prev => prev ? {
        ...prev,
        actionBar: {
          slots,
          activeBar: msg.activeBar ?? 1,
          mode: msg.mode,
          editable: msg.editable,
          ...(msg.layout ? { layout: msg.layout } : {}),
        },
      } : null);
    },
    questsUpdate: (msg: QuestsUpdateMessage) => {
      const active = msg?.active;
      if (!active || typeof active !== 'object') return;
      setPlayer(prev => {
        if (!prev) return null;
        const quests = { ...prev.quests, active: [...(prev.quests?.active ?? [])] };
        // Replace the active list with the canonical server snapshot;
        // each entry carries {id, currentCount} (quest names/descriptions
        // come from QUEST_DATABASE on the client). The resulting entries
        // land in the loosely-typed React Quest[] list. // typed-later
        // Rebuild each active quest from QUEST_DATABASE + wire counts, attaching
        // per-objective progress for multi-objective quests. // typed-later
        quests.active = Object.entries(active).flatMap(([id, v]: [string, QuestProgressWire]): unknown[] => {
          const q = toReactQuest(id, v, prev.class);
          return q ? [q] : [];
        }) as typeof quests.active;
        return { ...prev, quests };
      });
    },
    completedQuestsUpdate: (msg: CompletedQuestsUpdateMessage) => {
      if (!msg || !Array.isArray(msg.ids)) return;
      setPlayer(prev => prev ? {
        ...prev,
        quests: { ...prev.quests, completed: [...msg.ids!] },
      } : null);
    },
  });

  useEffect(() => {
    const unsubConnected = EventBus.getInstance().on('net:connected', (data: NetConnectedPayload) => {
      const sessionId = data.sessionId ?? null;
      activeHeavyFieldsSessionRef.current = sessionId;
      // React may still show the departing character; the new session's first
      // authoritative snapshot establishes its resource character identity.
      CooldownStore.beginChargeOwner(sessionId);
      activeHotbarAuthorityRef.current = null;
      setConnectedSessionId(sessionId);
      setHotbarHydratedSessionId(null);
      setIsConnected(true);
      setDisconnectReason(null);
      isConnecting.current = false;
      gameLogger.info('network', '[React] Connected event received', data);

      // Wire the combat-log pipeline. CombatLogService normalizes the
      // EventBus combat emissions; CombatLogChatBridge formats + pushes
      // into the chat 'combat' channel. Both idempotent — safe across
      // reconnect cycles. NetworkManager exposes the bound room on
      // window.__COLYSEUS_ROOM__ at this point. Both reads hit global
      // injections with no typed home. // typed-later
      const win = window as unknown as { __COLYSEUS_ROOM__?: Room; nm?: { getRoom?: () => Room | null } };
      const room = win.__COLYSEUS_ROOM__ ?? win.nm?.getRoom?.();
      // Peaceful is session-scoped server authority. Reset stale persisted
      // state on every connection, then seed from the current local schema
      // when it is already available (SERVER_STATE_UPDATE remains the durable
      // update path).
      const localSchema = sessionId
        ? (room as unknown as { state?: { players?: { get?: (id: string) => PlayerSchemaView | undefined } } })
          ?.state?.players?.get?.(sessionId)
        : undefined;
      setPlayer(prev => prev ? {
        ...prev,
        peaceful: typeof localSchema?.peaceful === 'boolean' ? localSchema.peaceful : false,
      } : null);
      if (room) {
        const svc = CombatLogService.getInstance();
        svc.attachRoom(room);
        svc.start();
        CombatLogChatBridge.getInstance().start();
      }
    });

    const unsubDisconnected = EventBus.getInstance().on('net:disconnected', (data: NetDisconnectedPayload) => {
      activeHeavyFieldsSessionRef.current = null;
      activeHotbarAuthorityRef.current = null;
      setConnectedSessionId(null);
      setHotbarHydratedSessionId(null);
      setIsConnected(false);
      // The in-flight guard must reset on a FAILED join too, not just after
      // net:connected — otherwise one server-side join rejection (e.g. a
      // NO_CHARACTER kick) leaves isConnecting true forever and every later
      // connectToServer() call silently no-ops (class-switch stall, 2026-06-12).
      isConnecting.current = false;
      setDisconnectReason(data?.reason || 'Unknown Disconnect');
      // Tear down the combat-log pipeline so a future reconnect re-binds
      // a fresh room without lingering listeners pointed at the old one.
      CombatLogChatBridge.getInstance().stop();
      CombatLogService.getInstance().detach();
      CooldownStore.beginChargeOwner(null);
      setPlayer(prev => prev ? {
        ...prev,
        peaceful: false,
        learnedAbilities: [],
        knownPlaces: [], claimedRewards: [],
        skillPoints: 0,
        talents: projectTalentTree(prev.class, {}),
        progressionCostOffers: undefined,
      } : null);
    });

    const unsubHeavyFieldsSnapshot = EventBus.getInstance().on(
      EVENTS.NET_MESSAGE_HEAVY_FIELDS_SNAPSHOT,
      (msg: HeavyFieldsSnapshotMessage) => {
        const ownerSessionId = activeHeavyFieldsSessionRef.current;
        if (!ownerSessionId || !msg) return;

        // Hydrate singleton-backed windows before entering React's state
        // updater; emitting InventorySystem events from inside the updater
        // causes a cross-component setState-during-render warning.
        if (msg.inventory) pushInventoryToSystem(msg.inventory);
        if (msg.abilityCharges) applyChargeMessage(msg.abilityCharges);

        const authoritativeHotbar = msg.hotbar && Array.isArray(msg.hotbar.slots)
          ? { ...msg.hotbar, slots: msg.hotbar.slots }
          : null;
        const hotbarAuthority = authoritativeHotbar
          ? readAtomicHotbarAuthority(msg, ownerSessionId)
          : null;
        if (authoritativeHotbar && hotbarAuthority) {
          // Install the effective permission before any later schema/periodic
          // update can race this snapshot. The same atomic authority is sent to
          // PlayerManager, which keeps keyboard/controller activation blocked
          // until it accepts this session owner.
          activeHotbarAuthorityRef.current = hotbarAuthority;
          pushHotbarSnapshotToCastSource(authoritativeHotbar, hotbarAuthority);
        }

        setPlayer(prev => {
          // React may defer this updater across a room replacement. Reject the
          // old session's queued work instead of hydrating the new player.
          if (activeHeavyFieldsSessionRef.current !== ownerSessionId) return prev;
          if (!prev) return null;
          // `next` accumulates partial wire→React-state mappings; the React
          // Character type is loose around quests/actionBar. // typed-later
          const next = { ...prev } as typeof prev & Record<string, unknown>;
          if (msg.inventory) {
            next.inventory = {
              ...prev.inventory,
              items: msg.inventory.items ?? [],
              gold: msg.inventory.gold ?? 0,
            };
          }
          if (authoritativeHotbar && hotbarAuthority) {
            next.level = hotbarAuthority.level;
            next.peaceful = hotbarAuthority.peaceful;
            next.actionBar = {
              slots: authoritativeHotbar.slots,
              activeBar: authoritativeHotbar.activeBar ?? 1,
              mode: hotbarAuthority.hotbarMode, editable: authoritativeHotbar.editable,
              ...(authoritativeHotbar.layout ? { layout: authoritativeHotbar.layout } : {}),
            };
          }
          if (msg.quests && typeof msg.quests.active === 'object' && msg.quests.active) {
            const quests = { ...prev.quests, active: [] as typeof prev.quests.active };
            quests.active = Object.entries(msg.quests.active).flatMap(([id, v]: [string, QuestProgressWire]) => {
              const q = toReactQuest(id, v, prev.class);
              // Fall back to a minimal entry for unknown ids (keeps prior behavior).
              return [q ?? { id, name: id, currentCount: v?.progress ?? 0, completed: false }];
            }) as typeof prev.quests.active;
            next.quests = quests;
          }
          if (msg.completedQuests && Array.isArray(msg.completedQuests.ids)) {
            next.quests = { ...(next.quests ?? prev.quests), completed: [...msg.completedQuests.ids] };
          }
          if (Array.isArray(msg.knownPlaces)) next.knownPlaces = normalizeKnownPlaces(msg.knownPlaces);
          if (Array.isArray(msg.claimedRewards)) next.claimedRewards = normalizeClaimedRewards(msg.claimedRewards);
          return keepUnchangedHeavyFields(prev, next);
        });

        // The HUD gate opens only after a valid server hotbar was applied.
        // Partial heavy snapshots must never reveal the persisted/default bar.
        if (
          authoritativeHotbar
          && hotbarAuthority
          && activeHeavyFieldsSessionRef.current === ownerSessionId
        ) {
          setHotbarHydratedSessionId(ownerSessionId);
        }
      },
    );

    const unsubRoomMeta = EventBus.getInstance().on('net:roomMeta', (data: unknown) => {
      gameLogger.info('network', '[React] Room metadata update', data);
    });

    const unsubStateUpdate = EventBus.getInstance().on(EVENTS.SERVER_STATE_UPDATE, ({ playerSchema }: { playerSchema?: PlayerSchemaView }) => {
      if (!playerSchema) return;
      writePlayerVitals(playerRef.current, playerSchema);
      setPlayer(prev => projectPlayerSchema(
        prev, playerSchema, activeHotbarAuthorityRef, activeHeavyFieldsSessionRef,
      ));
    });

    const unsubPeriodic = EventBus.getInstance().on('net:periodicSync', (data: PeriodicSyncPayload) => {
      writePlayerVitals(playerRef.current, data);
      setPlayer(prev => projectPeriodicSync(prev, data, activeHotbarAuthorityRef, activeHeavyFieldsSessionRef));
    });

    const unsubForceSync = EventBus.getInstance().on('net:message:forceSyncStats', (data: Partial<Helix2000Character>) => {
      writePlayerVitals(playerRef.current, data);
      setPlayer(prev => prev ? { ...prev, ...data } : null);
    });

    const unsubAbilityCharges = EventBus.getInstance().on(EVENTS.NET_MESSAGE_ABILITY_CHARGES_UPDATE, (message: unknown) => {
      if (activeHeavyFieldsSessionRef.current) applyChargeMessage(message);
    });

    const unsubQuestAccepted = EventBus.getInstance().on('net:message:questAccepted', (message: { quest?: QuestAcceptedPayload }) => {
      if (!message?.quest) return;
      const quest = message.quest;
      setPlayer(prev => {
        if (!prev) return null;
        const active = mergeAcceptedQuest(prev.quests?.active ?? [], quest);
        if (active === prev.quests?.active) return prev;
        return { ...prev, quests: { ...prev.quests, active } };
      });
      chatHandler.addMessage({ id: Date.now().toString(), sender: 'System', text: `Quest Accepted: ${quest.name}`, timestamp: Date.now(), type: 'system' });
    });

    // (relay-twins fix lane 2026-07-09) The old net:message:questUpdate twin
    // sub was deleted: it was starved by NetworkManager's direct handler AND
    // wrong for multi-objective quests (ignored objectiveIndex, clobbered the
    // quest-level currentCount). Quest progress reaches React via the full
    // `questsUpdate` snapshot the server sends alongside every questUpdate.

    const unsubQuestCompleted = EventBus.getInstance().on('net:message:questCompleted', (message: QuestCompletedMessage) => {
      setPlayer(prev => {
        if (!prev) return null;
        const quests = { ...prev.quests };
        const idx = quests.active.findIndex(q => q.id === message.questId);
        if (idx !== -1) {
          quests.completed.push(quests.active[idx].id);
          quests.active.splice(idx, 1);
        }
        return { ...prev, quests };
      });
      // No chat line here: the server's "Quest Completed: <name>!" system message is the one line.
    });

    const unsubLevelUp = EventBus.getInstance().on('net:message:levelUp', (message: LevelUpMessage) => {
      if (!message) return;
      setPlayer(prev => prev ? { ...prev, level: message.level } : null);
      chatHandler.addMessage({ id: Date.now().toString(), sender: 'System', text: `Level Up! You are now level ${message.level}!`, timestamp: Date.now(), type: 'system' });
    });

    // Level eligibility is not learning: training remains an explicit server
    // transaction. This global notice survives after the bounded FTUE ends.
    const unsubProgression = registerProgressionEvents(setPlayer);

    const unsubTeleport = EventBus.getInstance().on('net:message:playerTeleported', (message: TeleportChatMessage) => {
      const p = playerRef.current;
      const text = p && teleportChatLine(message, [p._id, p.id, (p as { sessionId?: string }).sessionId]);
      if (text) chatHandler.addMessage({ id: Date.now().toString(), sender: 'System', text, timestamp: Date.now(), type: 'system' });
    });

    const unsubChat = EventBus.getInstance().onDynamic('net:message:chatMessage', (message: { senderName: string; message: string; type?: ChatMessage['type']; senderId?: string }) => {
      chatHandler.addMessage({
        id: Date.now().toString() + Math.random(),
        sender: message.senderName,
        text: message.message,
        timestamp: Date.now(),
        type: message.type || 'global'
      });
      const gw = gameWorldRef.current;
      if (gw && message.senderId && !message.message.startsWith('/')) { // a command echo is not speech
        gw.showChatBubble(message.senderId, message.message);
      }
    });

    const unsubGold = EventBus.getInstance().on('net:message:goldReceived', (message: GoldReceivedMessage) => {
      // Gold itself is already authoritative in the inventoryUpdate sent
      // immediately before this cosmetic notification. Adding it here again
      // double-counted every pickup in the React HUD.
      // ChatHandler supplies a collision-resistant presentation id. A nearby
      // loot batch emits gold + several item notifications synchronously, so
      // Date.now() alone produces duplicate React keys.
      chatHandler.sendMessage(`💰 +${message.amount} Gold`, 'System', 'system');
    });

    const unsubLoot = EventBus.getInstance().on('net:message:lootReceived', (message: LootReceivedMessage) => {
      chatHandler.sendMessage(`📦 Received: ${message.name}`, 'System', 'system');
    });

    const unsubCorrection = EventBus.getInstance().on('net:message:positionCorrection', (message: { x: number; y: number; reason?: string }) => {
      setPlayer(prev => prev ? { ...prev, position: { ...prev.position, x: message.x, y: message.y } } : null);
      gameLogger.warn('network', `⚠️ Position corrected by server: ${message.reason}`);
    });

    return () => {
      unsubConnected(); unsubDisconnected(); unsubHeavyFieldsSnapshot(); unsubRoomMeta();
      unsubStateUpdate(); unsubPeriodic(); unsubForceSync(); unsubAbilityCharges();
      unsubQuestAccepted(); unsubQuestCompleted(); unsubLevelUp(); unsubProgression();
      unsubTeleport(); unsubChat(); unsubGold(); unsubLoot(); unsubCorrection();
    };
    // `player` + `gameWorld` intentionally not in deps — read via refs
    // above so the ~20 EventBus subscriptions register once, not once per
    // render. setState identities are stable.
  }, [setPlayer]);

  return {
    isConnected,
    isHotbarHydrated: !DevConfig.systems.multiplayer || (connectedSessionId !== null && hotbarHydratedSessionId === connectedSessionId),
    disconnectReason,
    joinRefusal,
    connectToServer
  };
}
